import React, { useEffect, useState } from "react";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from "@tanstack/react-query";
import { RecoilRoot, useRecoilValue } from "recoil";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { theme } from "styles/theme";
import {
  Experimental_CssVarsProvider as MaterialCssVarsProvider,
  THEME_ID as MATERIAL_THEME_ID,
} from "@mui/material/styles";
import { CssVarsProvider as JoyCssVarsProvider } from "@mui/joy/styles";
import { GlobalStyles } from "@mui/joy";
import { materialTheme } from "styles/materialTheme";
import "material-symbols";
import mixpanel from "mixpanel-browser";
import { BUILD_TARGET, GOOGLE_CLIENTID, isDev } from "config";
import useMixpanelWeb from "hooks/useMixpanelWeb";
import useSyncPromotionToken from "hooks/useSyncPromotionToken";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { initializeApp } from "firebase/app";
import { SendbirdWrapper } from "./sendbird/SendbirdWrapper";
import * as Sentry from "@sentry/react";
import { userAtom } from "recoil/userAtom";
import { setSentryUser } from "utils/sentry";
import ReactPixel from "react-facebook-pixel";
import {
  ensureFbcFromUrl,
  ensureFbp,
  FB_PIXEL_ID,
  getStoredUserAdvancedMatching,
  markAdvancedMatchingApplied,
  syncFbcToAccount,
} from "analytics/metaCapi";
import { ensureTwclidFromUrl } from "analytics/xAdsPixel";
import { captureChannelParamsFromUrl } from "analytics/channelParams";
import { getAdChannel } from "routes/adFunnel";
import { ChannelTalkProvider } from "contexts/ChannelTalkContext";
import {
  CS_INFO_DETAIL_ENDPOINT,
  CS_INFO_DOMAIN,
  csInfoApi,
} from "api/csInfoApi";
import { axiosInstance } from "api/instance";

// 라우팅에 필요한 모든 컴포넌트와 설정을 import 합니다.
import RootLayout from "./routes/RootLayout";
import { PATH } from "./routes/paths";
import Login from "pages/user/Login";
import Home from "pages/home/Home";
import TossPaymentsFail from "pages/tosspayments/TossPaymentsFail";
import TossPaymentsSuccess from "pages/tosspayments/TossPaymentsSuccess";
import TossPayments from "pages/tosspayments/TossPayments";
import AppleLoginRedirect from "pages/user/AppleLoginRedirect";
import KakaoLoginRedirect from "pages/login/KakaoLoginRedirect";
import SelfCheck from "pages/self-check/SelfCheck";
import SelfCheckEnd from "pages/self-check/SelfCheckEnd";
import NewsletterSettings from "pages/newsletter/NewsletterSettings";
import OnboardingScreen from "pages/onboarding/OnboardingScreen";
import EtcWhere from "pages/onboarding/optional/EtcWhere";
import YoutubeChannel from "pages/onboarding/optional/YoutubeChannel";
import AudioGuide from "pages/tool/guide/AudioGuide";
import PromotionBook from "pages/promotion/PromotionBook";
import ProductPage from "pages/product/ProductPage";
import PromotionBookPhoneNumberSubmit from "pages/promotion/PromotionBookPhoneNumberSubmit";
import SupportFaqListPage from "./pages/mypage/SupportFaqListPage";
import SupportFaqDetailPage from "./pages/mypage/SupportFaqDetailPage";
import AccountPage from "./pages/mypage/AccountPage";
import AppLockScreen from "./pages/security/AppLockScreen";
import PCLoginPage from "./pages/login/PCLoginPage";
import LoginCoach from "./pages/user/LoginCoach";
import AppLockSetupPage from "./pages/security/AppLockSetupPage";
import RefundScreen from "./pages/refund/RefundScreen";
import RefundSurveyTypeE from "./pages/refund/RefundSurveyTypeE";
import TossPaymentsPromotionSuccess from "./pages/tosspayments/TossPaymentsPromotionSuccess";
import SelfCommitmentSubmitPage from "./components/commitment/SelfCommitmentSubmitPage";
import TossPaymentsPlanUpgrade from "./pages/tosspayments/TossPaymentsPlanUpgrade";
import TossPaymentsPlanUpgradeSuccess from "./pages/tosspayments/TossPaymentsPlanUpgradeSuccess";
import TossPaymentsPlanUpgradeFail from "./pages/tosspayments/TossPaymentsPlanUpgradeFail";
import PlanUpgradeScreen from "./components/task/payment/PlanUpgradeScreen";
import PrivacyPolicy from "./pages/legal-documents/PrivacyPolicy";
import TermsOfService from "./pages/legal-documents/TermsOfService";
import OnboardingScreenPromotion from "./pages/onboarding/OnboardingScreenPromotion";
import PartnerPayment from "./pages/partner/PartnerPayment";
import PartnerLogin from "./pages/partner/PartnerLogin";
import PartnerNicknameSubmitPage from "./pages/partner/PartnerNicknameSubmitPage";
import PartnerTossPaymentsSuccess from "./pages/tosspayments/PartnerTossPaymentsSuccess";
import PartnerTossPaymentsFail from "./pages/tosspayments/PartnerTossPaymentsFail";
import EmailLoginPage from "./pages/login/EmailLoginPage";
import LoginPromotion from "./pages/user/LoginPromotion";
import PromotionCompleted from "./components/task/payment/PromotionCompleted";
import PromotionInitializer from "components/mkt/PromotionInitializer";
import PaymentFailPromotion from "./pages/promotion/PaymentFailPromotion";
import PromotionAlreadyUsed from "./components/task/payment/PromotionAlreadyUsed";
import KakaoLoginRedirectPromotion from "./pages/login/KakaoLoginRedirectPromotion";
import KakaoLoginRedirectReferral from "./pages/login/KakaoLoginRedirectReferral";
import ReferralEntryPage from "./pages/referral/ReferralEntryPage";
import ReferralLoginPage from "./pages/user/ReferralLoginPage";
import ReferralResult from "./components/task/payment/ReferralResult";
import AmbassadorDashboard from "./pages/ambassador/AmbassadorDashboard";
import AmbassadorLogin from "./pages/ambassador/AmbassadorLogin";
import AmbassadorContract from "./pages/ambassador/AmbassadorContract";
import AmbassadorContractSign from "./pages/ambassador/AmbassadorContractSign";
import AmbassadorTerms from "./pages/legal-documents/AmbassadorTerms";
import AmbassadorLink from "./pages/ambassador/AmbassadorLink";
import AdminPage from "./pages/admin/AdminPage";
import LetterPage from "pages/letter/LetterPage";
import TaskEditorAccessDenied from "pages/task-editor/TaskEditorAccessDenied";
import TaskEditorPage from "pages/task-editor/TaskEditorPage";
import { canAccessTaskEditor } from "pages/task-editor/utils/taskEditorAccess";
import TaskEditorStoreBootstrap from "components/common/TaskEditorStoreBootstrap";

export const firebaseConfig_dev = {
  apiKey: "AIzaSyAX_O-NntYzO4Bmh8M8G7PUpcP8cIwW8XI",
  authDomain: "distancing-dev.firebaseapp.com",
  projectId: "distancing-dev",
  storageBucket: "distancing-dev.appspot.com",
  messagingSenderId: "193897208164",
  appId: "1:193897208164:web:6c796023f17e225257c271",
  measurementId: "G-TXJBESPEG7",
};
export const firebaseConfig_real = {
  apiKey: "AIzaSyBMCcsN-xmAlgj6wnA1qW-S9Xlbuzdk4OI",
  authDomain: "distancing-404509.firebaseapp.com",
  projectId: "distancing-404509",
  storageBucket: "distancing-404509.appspot.com",
  messagingSenderId: "938509636319",
  appId: "1:938509636319:web:5be2e679961d04947e4f6a",
  measurementId: "G-DMCFDDT4V3",
};

const firebaseConfig = isDev ? firebaseConfig_dev : firebaseConfig_real;

function TaskEditorRouteGuard() {
  const user = useRecoilValue(userAtom);
  const canAccess = canAccessTaskEditor({
    buildTarget: BUILD_TARGET,
    hostname:
      typeof window === "undefined" ? undefined : window.location.hostname,
    user,
  });

  if (!canAccess) {
    return <TaskEditorAccessDenied />;
  }

  return (
    <>
      <TaskEditorStoreBootstrap />
      <TaskEditorPage />
    </>
  );
}

// createBrowserRouter를 사용하여 라우트 설정을 객체 형태로 정의합니다.
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: PATH.login, element: <Login /> },
      { path: PATH.emailLogin, element: <EmailLoginPage /> },
      { path: PATH.pcLogin, element: <PCLoginPage /> },
      { path: PATH.coachLogin, element: <LoginCoach /> },
      { path: PATH.applock, element: <AppLockScreen /> },
      { path: PATH.applockSetup, element: <AppLockSetupPage /> },
      { path: PATH.onboarding_new, element: <OnboardingScreen /> },
      { path: PATH.refund, element: <RefundScreen /> },
      { path: PATH.refundSurvey, element: <RefundSurveyTypeE /> },
      { path: PATH.etc_where, element: <EtcWhere /> },
      { path: PATH.youtube_channel, element: <YoutubeChannel /> },
      { path: PATH.home, element: <Home /> },
      { path: PATH.kakaoLoginRedirect, element: <KakaoLoginRedirect /> },
      {
        path: PATH.kakaoLoginRedirectPromotion,
        element: <KakaoLoginRedirectPromotion />,
      },
      {
        path: PATH.kakaoLoginRedirectGads,
        element: <KakaoLoginRedirectPromotion />,
      },
      {
        path: PATH.kakaoLoginRedirectReferral,
        element: <KakaoLoginRedirectReferral />,
      },
      { path: PATH.redirect_apple, element: <AppleLoginRedirect /> },
      { path: PATH.planUpgrade, element: <PlanUpgradeScreen /> },
      { path: PATH.tossPayments, element: <TossPayments /> },
      { path: PATH.privacyPolicy, element: <PrivacyPolicy /> },
      { path: PATH.termsOfService, element: <TermsOfService /> },
      { path: PATH.letter, element: <LetterPage /> },
      {
        path: PATH.tossPaymentsPlanUpgrade,
        element: <TossPaymentsPlanUpgrade />,
      },
      { path: PATH.tossPaymentsSuccess, element: <TossPaymentsSuccess /> },
      {
        path: PATH.tossPaymentsPlanUpgradeSuccess,
        element: <TossPaymentsPlanUpgradeSuccess />,
      },
      { path: PATH.tossPaymentsFail, element: <TossPaymentsFail /> },
      {
        path: PATH.tossPaymentsPlanUpgradeFail,
        element: <TossPaymentsPlanUpgradeFail />,
      },
      { path: PATH.newsletterSettings, element: <NewsletterSettings /> },
      { path: PATH.selfCheck, element: <SelfCheck /> },
      { path: PATH.selfCheckEnd, element: <SelfCheckEnd /> },
      { path: PATH.guide, element: <AudioGuide /> },
      { path: PATH.guideTw, element: <AudioGuide country="tw" /> },
      { path: PATH.product, element: <ProductPage /> },
      { path: PATH.promotionBook, element: <PromotionBook /> },
      { path: PATH.account, element: <AccountPage /> },
      { path: PATH.support, element: <SupportFaqListPage /> },
      { path: PATH.supportDetail, element: <SupportFaqDetailPage /> },
      { path: PATH.taskEditor, element: <TaskEditorRouteGuard /> },
      { path: PATH.commitment, element: <SelfCommitmentSubmitPage /> },
      {
        path: PATH.promotionPhoneNumberSubmit,
        element: <PromotionBookPhoneNumberSubmit />,
      },
      {
        path: PATH.promotionOnboarding,
        element: <OnboardingScreenPromotion />,
      },
      { path: PATH.promotionLogin, element: <LoginPromotion /> },
      {
        path: PATH.promotionTosspaymentsSuccess,
        element: <TossPaymentsPromotionSuccess />,
      },
      {
        path: PATH.promotionTosspaymentsFail,
        element: <PaymentFailPromotion />,
      },
      {
        path: PATH.promotionCompleted,
        element: <PromotionCompleted />,
      },
      {
        path: PATH.promotionAlreadyUsed,
        element: <PromotionAlreadyUsed />,
      },
      // Google Ads 전용 웹퍼널 — /promotion-* 와 동일 화면, 광고 픽셀만 분리 (routes/adFunnel.ts)
      { path: PATH.gadsOnboarding, element: <OnboardingScreenPromotion /> },
      { path: PATH.gadsLogin, element: <LoginPromotion /> },
      {
        path: PATH.gadsTosspaymentsSuccess,
        element: <TossPaymentsPromotionSuccess />,
      },
      { path: PATH.gadsTosspaymentsFail, element: <PaymentFailPromotion /> },
      { path: PATH.gadsCompleted, element: <PromotionCompleted /> },
      { path: PATH.gadsAlreadyUsed, element: <PromotionAlreadyUsed /> },
      { path: PATH.referralEntry, element: <ReferralEntryPage /> },
      { path: PATH.referralLogin, element: <ReferralLoginPage /> },
      { path: PATH.referralResult, element: <ReferralResult /> },
      { path: PATH.partnerPayment, element: <PartnerPayment /> },
      { path: PATH.partnerLogin, element: <PartnerLogin /> },
      {
        path: PATH.partnerTosspaymentsSuccess,
        element: <PartnerTossPaymentsSuccess />,
      },
      {
        path: PATH.partnerTosspaymentsFail,
        element: <PartnerTossPaymentsFail />,
      },
      {
        path: PATH.partnerNicknameSubmit,
        element: <PartnerNicknameSubmitPage />,
      },
      { path: PATH.ambassador, element: <AmbassadorDashboard /> },
      { path: PATH.ambassadorLogin, element: <AmbassadorLogin /> },
      { path: PATH.ambassadorContract, element: <AmbassadorContract /> },
      {
        path: PATH.ambassadorContractSign,
        element: <AmbassadorContractSign />,
      },
      { path: PATH.ambassadorTerms, element: <AmbassadorTerms /> },
      { path: PATH.ambassadorLink, element: <AmbassadorLink /> },
      { path: PATH.admin, element: <AdminPage /> },
    ],
  },
]);

// Sentry user context를 설정하는 컴포넌트
function SentryUserSync() {
  const user = useRecoilValue(userAtom);

  useEffect(() => {
    setSentryUser(user?.userId);
  }, [user?.userId]);

  return null;
}

// ChannelTalk Provider Wrapper
function ChannelTalkWrapper({ children }: { children: React.ReactNode }) {
  const user = useRecoilValue(userAtom);

  const { data } = useQuery(
    [CS_INFO_DOMAIN, CS_INFO_DETAIL_ENDPOINT],
    () => csInfoApi.getDetail(),
    {
      enabled:
        !!axiosInstance.defaults.headers["X-Auth-Token"] && !!user?.accessToken,
    }
  );

  const channelUserInfo = React.useMemo(() => {
    const info = data?.result;
    if (info?.userId && info.nickname) {
      return {
        id: String(info.userId),
        name: info.nickname,
        email: info.email,
        coachName: info.coachName,
        subscriptionStartDate: info.subscriptionStartDate,
        subscriptionPayAmount: info.subscriptionPayAmount,
        subscriptionExpirationDate: info.subscriptionExpirationDate,
        subscriptionStatus: info.subscriptionStatus,
        subscriptionCount: info.subscriptionCount,
        plan: info.plan,
        hasBeenRefunded: info.hasBeenRefunded,
      };
    }
    return null;
  }, [data?.result]);

  return (
    <ChannelTalkProvider userInfo={channelUserInfo} enabled={true}>
      {children}
    </ChannelTalkProvider>
  );
}

// axiosInstancePromotion의 토큰 동기화를 위한 컴포넌트
function PromotionTokenSync() {
  useSyncPromotionToken();
  return null;
}

function App() {
  const [queryClient] = useState(() => new QueryClient());
  const body = document.body;
  body.style.position = "fixed";
  body.style.overflow = "hidden";
  const isHome = window.location.pathname === "/";

  const isPromotionPath =
    window.location.pathname.startsWith("/promotion-") ||
    window.location.pathname.startsWith("/promotion/oauth/") ||
    window.location.pathname.startsWith("/gads-") ||
    window.location.pathname.startsWith("/gads/oauth/") ||
    window.location.pathname.startsWith("/with/") ||
    window.location.pathname.startsWith("/referral");

  const setMixPanel_come = useMixpanelWeb({
    eventName: "진입",
  });

  useEffect(() => {
    if (isHome) {
      setMixPanel_come();
    }
  }, [isHome, setMixPanel_come]);

  const googleClientid = GOOGLE_CLIENTID;

  initializeApp(firebaseConfig);

  // 웹퍼널 광고 채널 — 메타 퍼널(/promotion-*, 레퍼럴)은 Meta+X, gads 퍼널은 gtag 만 (routes/adFunnel.ts)
  const adChannel = getAdChannel();
  const isMetaFunnelPath = isPromotionPath && adChannel === "meta";

  // Initialize Google Tag (gtag) only in Real environment
  // 메타 퍼널에서는 스크립트 자체를 로드하지 않는다 — Google Ads 에 페이지뷰·전환이 섞이지 않도록
  useEffect(() => {
    if (BUILD_TARGET === "real" && !isMetaFunnelPath) {
      // 1. 중복 실행 방지 (이미 스크립트가 있으면 종료)
      if (document.getElementById("google-gtag-script")) return;

      // 인앱 WebView 에서는 gtag 를 아예 로드하지 않는다. Google Ads 전환 3종은 모두 브라우저 gads 퍼널에서만 나가고,
      // WebView 는 쿠키 저장소가 Chrome·YouTube 와 분리돼 리마케팅 히트가 광고 타겟팅에 연결되지 않는다 — 앱 유저의
      // 활동지 페이지뷰만 Google Ads 로 흘러가던 것을 막는다. GA4 는 네이티브 Firebase 가 앱 스트림으로 보낸다(중복 방지).
      // 인앱 판별은 Settings.tsx 가 permanentDataAtom.isWebview 를 정할 때 쓰는 식과 동일 — 네이티브 앱이 페이지에 심는
      // 브리지 객체(RN WebView / iOS WKWebView messageHandlers / Android JS interface) 중 하나라도 있으면 앱 안이다.
      // useIsInappWebview 훅(atom)은 이 effect 시점에 아직 undefined 일 수 있어 window 를 직접 본다.
      const isInappWebview =
        !!window.ReactNativeWebView ||
        !!window.webkit?.messageHandlers ||
        !!window.AndroidInterface;
      if (isInappWebview) return;

      // 2. dataLayer 및 gtag 함수 즉시 초기화
      // (스크립트 로딩을 기다리지 않고 명령을 미리 쌓아둡니다 -> 데이터 유실 방지)
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };

      // 3. 초기 설정 즉시 실행
      window.gtag("js", new Date());
      window.gtag("config", "AW-10862597581", {
        // 향상된 전환(Enhanced Conversions) — useGtagConversion 이 set 하는 user_data 를 전송하도록 허용
        allow_enhanced_conversions: true,
      });
      // GA4 웹 스트림 — Firebase 앱과 같은 GA4 속성에 웹 세션(gclid 포함)을 남겨, 웹퍼널 이탈 후 앱에서 결제한 유저를
      // Google 이 크로스 플랫폼으로 이을 재료를 만든다.
      window.gtag("config", firebaseConfig.measurementId);

      // 4. 스크립트 비동기 로드
      const script = document.createElement("script");
      script.id = "google-gtag-script"; // ID 부여
      script.src = "https://www.googletagmanager.com/gtag/js?id=AW-10862597581";
      script.async = true;
      document.head.appendChild(script);

      console.log("[gtag] Initialized in Real environment");
    }
  }, [isMetaFunnelPath]);

  // 메타 퍼널 전용: Meta Pixel(PageView 포함) + X Ads Pixel. gads 퍼널에서는 어느 것도 초기화하지 않는다.
  if (isMetaFunnelPath) {
    ensureFbcFromUrl();
    ensureFbp();
    ensureTwclidFromUrl();

    // 로그인 사용자가 저장돼 있으면 최초 init부터 Advanced Matching 탑재 (PageView 포함 EMQ 개선)
    const pixelAdvancedMatching = getStoredUserAdvancedMatching();
    ReactPixel.init(
      FB_PIXEL_ID,
      pixelAdvancedMatching as unknown as Parameters<typeof ReactPixel.init>[1],
      {
        autoConfig: false, // 자동 설정
        debug: isDev, // 디버그 모드
      }
    );
    markAdvancedMatchingApplied(pixelAdvancedMatching);
    // 주의: 프로모션 세션의 실제 유저는 userSimpleAtom(메모리 전용)이라 init 시점엔 읽을 수 없다.
    // getStoredUserAdvancedMatching()은 메인앱 userAtom("user" 저장소)이라 여기서 sync하면 엉뚱한 계정에
    // 매핑된다 — 프로모션 랜딩의 fbc sync는 userSimple userId를 쓰는 useAutoLoginPromotion이 담당한다.
    // Trigger PageView to ensure fbp cookie is created
    ReactPixel.pageView();
    if (typeof window !== "undefined" && (window as any).fbq) {
      (window as any).fbq.disablePushState = true;
    }

    // Initialize X(Twitter) Ads Pixel only in Real environment
    if (
      BUILD_TARGET === "real" &&
      typeof document !== "undefined" &&
      !document.getElementById("x-ads-pixel-script")
    ) {
      (function (e: any, t: Document, n: string) {
        let s: any, u: any, a: any;
        if (!e.twq) {
          s = e.twq = function () {
            s.exe ? s.exe.apply(s, arguments) : s.queue.push(arguments);
          };
          s.version = "1.1";
          s.queue = [];
          u = t.createElement(n) as HTMLScriptElement;
          u.async = true;
          u.id = "x-ads-pixel-script";
          u.src = "https://static.ads-twitter.com/uwt.js";
          a = t.getElementsByTagName(n)[0];
          a.parentNode.insertBefore(u, a);
        }
      })(window, document, "script");
      (window as any).twq("config", "ogz8m");
    }
  }

  if (isPromotionPath) {
    const marketingProjectId = isDev
      ? "671725f1abeed2fd78dbf21de4739bcd"
      : "89f46b9084531b79079079d885af942f";
    // 유입 채널(utm/클릭 ID)을 SPA 내비게이션으로 URL이 사라지기 전에 캡처 — 가입 시 서버 저장용 (채널 무관)
    captureChannelParamsFromUrl();

    mixpanel.init(
      marketingProjectId,
      {
        debug: isDev,
        api_method: "POST",
        api_transport: "XHR",
        batch_requests: false,
        ignore_dnt: false,
        disable_persistence: false,
        persistence: "cookie",
        cookie_domain: "",
        cookie_expiration: 365,
        cross_site_cookie: false,
        cross_subdomain_cookie: true,
        ip: true,
        opt_out_tracking_by_default: false,
        opt_out_persistence_by_default: false,
        opt_out_tracking_persistence_type: "localStorage",
        opt_out_tracking_cookie_prefix: undefined,
        persistence_name: "",
        property_blacklist: [],
        secure_cookie: false,
        track_links_timeout: 300,
        upgrade: false,
        xhr_headers: {},
      },
      "promotion"
    );

    console.log("[Mixpanel] Initialized promotion project only");
  } else {
    ensureFbp();

    // 로그인 사용자가 저장돼 있으면 최초 init부터 Advanced Matching 탑재 (EMQ 개선)
    const pixelAdvancedMatching = getStoredUserAdvancedMatching();
    ReactPixel.init(
      FB_PIXEL_ID,
      pixelAdvancedMatching as unknown as Parameters<typeof ReactPixel.init>[1],
      {
        autoConfig: false, // 자동 설정
        debug: isDev, // 디버그 모드
      }
    );
    markAdvancedMatchingApplied(pixelAdvancedMatching);
    // 기로그인 유저가 새 광고 클릭으로 메인 경로에 랜딩해도 서버 저장 fbc를 last-click으로 갱신
    // (인앱 웹뷰는 fbc 쿠키·fbclid가 없어 자동 no-op)
    syncFbcToAccount(pixelAdvancedMatching?.external_id);

    const mainProjectId = isDev
      ? "306a1663b1fece6e28324733a9aca2ee"
      : "6bb06d65a8dec1d9dec0496adef7c8d0";

    mixpanel.init(mainProjectId, {
      debug: isDev,
      api_method: "POST",
      api_transport: "XHR",
      batch_requests: false,
      batch_size: 50,
      batch_flush_interval_ms: 5000,
      batch_request_timeout_ms: 90000,
      record_sessions_percent: 0, // 수동 제어
      record_heatmap_data: true,
      cookie_domain: "",
      cookie_expiration: 365,
      cross_site_cookie: false,
      cross_subdomain_cookie: true,
      disable_persistence: false,
      ip: true,
      opt_out_tracking_by_default: false,
      opt_out_persistence_by_default: false,
      opt_out_tracking_persistence_type: "localStorage",
      opt_out_tracking_cookie_prefix: undefined,
      persistence: "cookie",
      persistence_name: "",
      property_blacklist: [],
      secure_cookie: false,
      track_links_timeout: 300,
      upgrade: false,
      xhr_headers: {},
      ignore_dnt: false,
    });
  }

  return (
    <GoogleOAuthProvider clientId={googleClientid}>
      <MaterialCssVarsProvider
        defaultMode="system"
        theme={{ [MATERIAL_THEME_ID]: materialTheme }}
      >
        <JoyCssVarsProvider defaultMode="system" theme={theme}>
          <GlobalStyles
            styles={{
              html: {
                boxSizing: "border-box",
              },
              body: {},
            }}
          />
          <RecoilRoot>
            <Sentry.ErrorBoundary
              fallback={
                isDev
                  ? ({ error, resetError }) => (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100vh",
                          padding: "20px",
                        }}
                      >
                        <h2>문제가 발생했습니다</h2>
                        <p>잠시 후 다시 시도해주세요.</p>
                        <p>{error.message}</p>
                        <button
                          onClick={resetError}
                          style={{
                            marginTop: "20px",
                            padding: "10px 20px",
                            cursor: "pointer",
                          }}
                        >
                          다시 시도
                        </button>
                      </div>
                    )
                  : undefined
              }
            >
              <SentryUserSync />
              <PromotionTokenSync />
              {isPromotionPath && <PromotionInitializer />}
              <QueryClientProvider client={queryClient}>
                <ChannelTalkWrapper>
                  <SendbirdWrapper>
                    <RouterProvider router={router} />
                  </SendbirdWrapper>
                </ChannelTalkWrapper>
              </QueryClientProvider>
            </Sentry.ErrorBoundary>
          </RecoilRoot>
        </JoyCssVarsProvider>
      </MaterialCssVarsProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
